// client/src/pages/admin/AdminDocumentForm.js
// Create or edit a manual invoice, quote, or proforma invoice.
//   /admin/documents/new/:type  - new document of that type
//   /admin/documents/:id/edit   - edit an existing one
import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Box, Typography, Button, Stack, Grid, TextField, Autocomplete, IconButton, Alert, CircularProgress,
  Table, TableHead, TableRow, TableCell, TableBody, Divider, Paper,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { AuthContext } from '../../context/AuthContext';
import { formatCurrency } from '../../utils/currency';
import { API_BASE_URL } from '../../utils/media';
import { DOCUMENT_TYPES, isLockedDocument } from '../../utils/documents';

const VAT_RATE = 0.15;

let nextLineKey = 1;
const emptyLine = () => ({ key: `line${nextLineKey++}`, productId: null, description: '', quantity: '1', unitPrice: '' });

const todayIso = () => {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};

const emptyForm = () => ({
  customerUserId: null,
  customerName: '',
  customerCompany: '',
  customerEmail: '',
  customerPhone: '',
  customerVatNumber: '',
  customerAddress: '',
  issueDate: todayIso(),
  dueDate: '',
  discountAmount: '',
  notes: '',
});

const roundMoney = (amount) => Math.round(amount * 100) / 100;

const AdminDocumentForm = () => {
  const { id, type: typeParam } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);

  const [type, setType] = useState(typeParam || null);
  const [number, setNumber] = useState('');
  const [locked, setLocked] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [lines, setLines] = useState(() => [emptyLine()]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  // Customers and products only feed the autofill pickers - the form still
  // works (typed by hand) if either fails to load.
  useEffect(() => {
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    fetch(`${API_BASE_URL}/api/admin/users`, { headers })
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => setCustomers(Array.isArray(data) ? data : []))
      .catch(() => {});
    fetch(`${API_BASE_URL}/api/admin/products`, { headers })
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => setProducts(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [token]);

  useEffect(() => {
    if (!isEdit || !token) return;
    (async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/admin/documents/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const doc = await response.json();
        if (!response.ok) throw new Error(doc.error || 'Failed to load the document.');
        setType(doc.type);
        setNumber(doc.number);
        setLocked(isLockedDocument(doc));
        setForm({
          customerUserId: doc.customer_user_id,
          customerName: doc.customer_name || '',
          customerCompany: doc.customer_company || '',
          customerEmail: doc.customer_email || '',
          customerPhone: doc.customer_phone || '',
          customerVatNumber: doc.customer_vat_number || '',
          customerAddress: doc.customer_address || '',
          issueDate: doc.issue_date,
          dueDate: doc.due_date || '',
          discountAmount: Number(doc.discount_amount) ? String(Number(doc.discount_amount)) : '',
          notes: doc.notes || '',
        });
        setLines(doc.items.map((item) => ({
          key: `line${nextLineKey++}`,
          productId: item.product_id,
          description: item.description,
          quantity: String(Number(item.quantity)),
          unitPrice: String(Number(item.unit_price)),
        })));
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isEdit, token]);

  const meta = type ? DOCUMENT_TYPES[type] : null;

  const setField = (name) => (e) => setForm((prev) => ({ ...prev, [name]: e.target.value }));
  const updateLine = (key, changes) => setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...changes } : l)));
  const removeLine = (key) => setLines((prev) => (prev.length > 1 ? prev.filter((l) => l.key !== key) : prev));

  const pickCustomer = (customer) => {
    if (!customer) {
      setForm((prev) => ({ ...prev, customerUserId: null }));
      return;
    }
    setForm((prev) => ({
      ...prev,
      customerUserId: customer.id,
      customerName: customer.company || customer.email,
      customerCompany: customer.company || '',
      customerEmail: customer.email || '',
      customerPhone: customer.phone || '',
      customerAddress: customer.address || '',
    }));
  };

  const lineTotal = (line) => roundMoney((Number(line.quantity) || 0) * (Number(line.unitPrice) || 0));
  const subtotal = roundMoney(lines.reduce((sum, line) => sum + lineTotal(line), 0));
  const discount = roundMoney(Number(form.discountAmount) || 0);
  const total = roundMoney(Math.max(subtotal - discount, 0));
  const vat = roundMoney(total * VAT_RATE / (1 + VAT_RATE));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/documents${isEdit ? `/${id}` : ''}`, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          type,
          ...form,
          items: lines.map(({ productId, description, quantity, unitPrice }) => ({ productId, description, quantity, unitPrice })),
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to save.');
      navigate(`/documents/${data.id}`);
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  if (loading) return <CircularProgress size={28} />;
  if (!meta) return <Alert severity="error">{error || 'Unknown document type.'}</Alert>;

  const selectedCustomer = customers.find((c) => c.id === form.customerUserId) || null;

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Button component={Link} to={meta.listPath} startIcon={<ArrowBackIcon />}>{meta.plural}</Button>
      </Stack>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
        {isEdit ? `Edit ${meta.label} ${number}` : `New ${meta.label}`}
      </Typography>

      {locked && (
        <Alert severity="info" sx={{ mb: 2 }}>This invoice is paid or cancelled, so it can no longer be edited.</Alert>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <fieldset disabled={locked} style={{ border: 0, margin: 0, padding: 0, minWidth: 0 }}>
        <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>Customer</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Autocomplete
                options={customers}
                value={selectedCustomer}
                onChange={(e, value) => pickCustomer(value)}
                getOptionLabel={(c) => (c.company ? `${c.company} (${c.email})` : c.email)}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                disabled={locked}
                renderInput={(params) => (
                  <TextField {...params} label="Existing customer (optional)" helperText="Pick one to fill in their details, or type them below." />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Customer name" required fullWidth value={form.customerName} onChange={setField('customerName')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Company" fullWidth value={form.customerCompany} onChange={setField('customerCompany')} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="Email" type="email" fullWidth value={form.customerEmail} onChange={setField('customerEmail')} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="Phone" fullWidth value={form.customerPhone} onChange={setField('customerPhone')} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="Customer VAT number" fullWidth value={form.customerVatNumber} onChange={setField('customerVatNumber')} />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Address" fullWidth multiline minRows={2} value={form.customerAddress} onChange={setField('customerAddress')} />
            </Grid>
          </Grid>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Date" type="date" required fullWidth InputLabelProps={{ shrink: true }}
                value={form.issueDate} onChange={setField('issueDate')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={`${meta.dueLabel} (optional)`} type="date" fullWidth InputLabelProps={{ shrink: true }}
                value={form.dueDate} onChange={setField('dueDate')}
              />
            </Grid>
          </Grid>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
          <Typography variant="subtitle1" fontWeight={700}>Line items</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Prices include VAT, the same as on the shop. Pick a product or type any description.
          </Typography>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 640 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '50%' }}>Description</TableCell>
                  <TableCell sx={{ width: 100 }}>Qty</TableCell>
                  <TableCell sx={{ width: 140 }}>Unit price (R)</TableCell>
                  <TableCell align="right" sx={{ width: 120 }}>Total</TableCell>
                  <TableCell sx={{ width: 48 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {lines.map((line) => (
                  <TableRow key={line.key}>
                    <TableCell>
                      <Autocomplete
                        freeSolo
                        options={products}
                        getOptionLabel={(p) => (typeof p === 'string' ? p : p.name)}
                        filterOptions={(options, state) => options
                          .filter((p) => p.name.toLowerCase().includes(state.inputValue.toLowerCase()))
                          .slice(0, 20)}
                        inputValue={line.description}
                        onInputChange={(e, value, reason) => {
                          if (reason === 'input' || reason === 'clear') updateLine(line.key, { description: value, productId: null });
                        }}
                        onChange={(e, value) => {
                          if (value && typeof value === 'object') {
                            updateLine(line.key, { description: value.name, productId: value.id, unitPrice: String(Number(value.price)) });
                          }
                        }}
                        disabled={locked}
                        renderOption={(props, p) => (
                          <li {...props} key={p.id}>
                            <Stack direction="row" justifyContent="space-between" sx={{ width: '100%' }}>
                              <span>{p.name}</span>
                              <Typography variant="body2" color="text.secondary">{formatCurrency(p.price)}</Typography>
                            </Stack>
                          </li>
                        )}
                        renderInput={(params) => <TextField {...params} size="small" placeholder="Product or description" required />}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small" type="number" required value={line.quantity}
                        inputProps={{ min: 0.01, step: 'any' }}
                        onChange={(e) => updateLine(line.key, { quantity: e.target.value })}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small" type="number" required value={line.unitPrice}
                        inputProps={{ min: 0, step: 0.01 }}
                        onChange={(e) => updateLine(line.key, { unitPrice: e.target.value })}
                      />
                    </TableCell>
                    <TableCell align="right">{formatCurrency(lineTotal(line))}</TableCell>
                    <TableCell>
                      <IconButton
                        size="small" aria-label="Remove line"
                        onClick={() => removeLine(line.key)} disabled={locked || lines.length === 1}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
          <Button startIcon={<AddIcon />} onClick={() => setLines((prev) => [...prev, emptyLine()])} sx={{ mt: 1 }} disabled={locked}>
            Add line
          </Button>

          <Divider sx={{ my: 2 }} />

          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} md={7}>
              <TextField
                label="Notes (shown on the document)" fullWidth multiline minRows={3}
                placeholder="e.g. banking details, payment terms, delivery notes"
                value={form.notes} onChange={setField('notes')}
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <Stack spacing={1}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.secondary">Subtotal</Typography>
                  <Typography>{formatCurrency(subtotal)}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography color="text.secondary">Discount (R)</Typography>
                  <TextField
                    size="small" type="number" value={form.discountAmount} onChange={setField('discountAmount')}
                    inputProps={{ min: 0, step: 0.01 }} sx={{ width: 140 }}
                  />
                </Stack>
                <Divider />
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="h6" fontWeight={700}>Total</Typography>
                  <Typography variant="h6" fontWeight={700}>{formatCurrency(total)}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">Includes VAT (15%)</Typography>
                  <Typography variant="body2" color="text.secondary">{formatCurrency(vat)}</Typography>
                </Stack>
              </Stack>
            </Grid>
          </Grid>
        </Paper>
      </fieldset>

      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <Button component={Link} to={isEdit ? `/documents/${id}` : meta.listPath}>Cancel</Button>
        <Button type="submit" variant="contained" disabled={saving || locked}>
          {isEdit ? 'Save changes' : `Create ${meta.label}`}
        </Button>
      </Stack>
    </Box>
  );
};

export default AdminDocumentForm;
